import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://linkthemissing.com';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1542382257-80dedb725088?w=1200&h=630&fit=crop';

/**
 * SEO Component - Handles meta tags and Schema.org structured data
 */
export const SEO = ({
  title,
  description,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  schema,
  noIndex = false,
  children
}) => {
  const fullTitle = title ? `${title} | LinkTheMissing` : 'LinkTheMissing - Every Missing Person Deserves to Be Found';
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="LinkTheMissing" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

/**
 * Generate Schema.org MissingPerson structured data
 * https://schema.org/MissingPerson (proposed)
 * Falls back to Person with additionalType
 */
export const generateMissingPersonSchema = (caseData) => {
  if (!caseData) return null;
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    'additionalType': 'MissingPerson',
    'name': caseData.name,
    'description': caseData.description || `Missing person case for ${caseData.name}`,
    'url': `${SITE_URL}/case/${caseData.id}`,
  };
  
  // Add image if available
  if (caseData.photo_url || caseData.photo_base64) {
    schema.image = caseData.photo_url || caseData.photo_base64;
  }
  
  // Add age/birthDate approximation
  if (caseData.age) {
    schema.description += `. Age: ${caseData.age}`;
  }
  
  // Add gender
  if (caseData.gender) {
    schema.gender = caseData.gender;
  }
  
  // Add physical characteristics
  if (caseData.height) {
    schema.height = caseData.height;
  }
  if (caseData.weight) {
    schema.weight = caseData.weight;
  }
  
  // Add last known location
  if (caseData.last_seen_location || caseData.state) {
    schema.homeLocation = {
      '@type': 'Place',
      'name': caseData.last_seen_location || caseData.state,
    };
    
    // Add coordinates if available
    if (caseData.latitude && caseData.longitude) {
      schema.homeLocation.geo = {
        '@type': 'GeoCoordinates',
        'latitude': caseData.latitude,
        'longitude': caseData.longitude
      };
    }
  }
  
  // Wrap in SearchAction for better discovery
  return {
    '@context': 'https://schema.org',
    '@graph': [
      schema,
      {
        '@type': 'WebPage',
        'name': `Missing: ${caseData.name}`,
        'description': `Help find ${caseData.name}. ${caseData.last_seen_location ? `Last seen in ${caseData.last_seen_location}.` : ''} Submit tips anonymously.`,
        'url': `${SITE_URL}/case/${caseData.id}`,
        'mainEntity': schema,
        'datePublished': caseData.created_at,
        'dateModified': caseData.updated_at || caseData.created_at,
      }
    ]
  };
};

/**
 * Generate Organization schema for the site
 */
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'name': 'LinkTheMissing',
  'url': SITE_URL,
  'logo': `${SITE_URL}/logo.png`,
  'description': 'A privately-funded platform connecting communities to help locate missing persons nationwide.',
  'sameAs': [
    'https://twitter.com/linkthemissing',
    'https://facebook.com/linkthemissing',
    'https://instagram.com/linkthemissing'
  ],
  'contactPoint': {
    '@type': 'ContactPoint',
    'contactType': 'customer service',
    'email': 'contact@linkthemissing.com',
    'availableLanguage': 'English'
  }
});

/**
 * Generate WebSite schema with search action
 */
export const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  'name': 'LinkTheMissing',
  'url': SITE_URL,
  'description': 'Search across 62,000+ missing person cases from NamUs, NCMEC, Charley Project, and Doe Network.',
  'potentialAction': {
    '@type': 'SearchAction',
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': `${SITE_URL}/browse?q={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  }
});

/**
 * Generate BreadcrumbList schema
 */
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': items.map((item, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': item.name,
    'item': item.url ? `${SITE_URL}${item.url}` : undefined
  }))
});

/**
 * Generate FAQPage schema
 */
export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': faqs.map(faq => ({
    '@type': 'Question',
    'name': faq.question,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': faq.answer
    }
  }))
});

/**
 * Generate Article schema for blog/news posts
 */
export const generateArticleSchema = (article) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  'headline': article.title,
  'description': article.description,
  'image': article.image,
  'datePublished': article.publishedAt,
  'dateModified': article.updatedAt || article.publishedAt,
  'author': {
    '@type': 'Organization',
    'name': 'LinkTheMissing'
  },
  'publisher': {
    '@type': 'Organization',
    'name': 'LinkTheMissing',
    'logo': {
      '@type': 'ImageObject',
      'url': `${SITE_URL}/logo.png`
    }
  }
});

export default SEO;
